import { CartAddEvent } from '@theme/events';

const STORAGE_KEY = 'rd_wishlist_products_v1';
const PRODUCT_CARD_STORAGE_KEY = 'rd-saved-products';
const CHANGE_EVENT = 'rd:wishlist:change';
const WISHLIST_URL = '/pages/wishlist';
const LEGACY_KEYS = [
  'wishlist',
  'wishlistItems',
  'wishlist_products',
  'shopify_wishlist',
  'rdWishlist',
  'tinkerWishlist',
  'favorites',
  'favourites',
  'rd-saved-products',
];

const heartIcon = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
  </svg>
`;

const bookmarkIcon = `
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 4.75A2.75 2.75 0 0 1 8.75 2h6.5A2.75 2.75 0 0 1 18 4.75V22l-6-3.75L6 22V4.75Z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
  </svg>
`;

function getRootUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function escapeSelector(value) {
  if (window.CSS?.escape) return window.CSS.escape(String(value));
  return String(value).replace(/["\\]/g, '\\$&');
}

function getHandleFromUrl(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    const match = url.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).trim() : '';
  } catch (_error) {
    const match = String(value).match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).trim() : '';
  }
}

function normalizeHandle(value) {
  if (!value) return '';
  const fromUrl = getHandleFromUrl(String(value));
  if (fromUrl) return fromUrl;
  return String(value).trim().replace(/^\/+|\/+$/g, '').split(/[?#]/)[0];
}

function normalizeStoredItem(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const handle = normalizeHandle(value);
    return handle ? { handle, addedAt: Date.now() } : null;
  }

  if (typeof value !== 'object') return null;

  const handle = normalizeHandle(
    value.handle ||
      value.product_handle ||
      value.productHandle ||
      value.slug ||
      value.url ||
      value.product_url ||
      value.productUrl
  );

  if (!handle) return null;

  return {
    handle,
    productId: value.productId || value.product_id || value.id || '',
    variantId: value.variantId || value.variant_id || '',
    title: value.title || value.product_title || '',
    url: value.url || value.product_url || `${getRootUrl()}products/${handle}`,
    image: value.image || value.featured_image || value.featuredImage || '',
    price: Number(value.price || 0),
    compareAtPrice: Number(value.compareAtPrice || value.compare_at_price || 0),
    available: value.available !== false,
    addedAt: Number(value.addedAt || value.added_at || Date.now()),
  };
}

function dedupeItems(items) {
  const seen = new Set();
  const output = [];

  for (const value of items) {
    const item = normalizeStoredItem(value);
    if (!item || seen.has(item.handle)) continue;
    seen.add(item.handle);
    output.push(item);
  }

  return output.sort((a, b) => Number(b.addedAt || 0) - Number(a.addedAt || 0));
}

function safeParse(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function readStorage() {
  try {
    const rawValue = localStorage.getItem(STORAGE_KEY);

    if (rawValue === null) return null;

    const saved = safeParse(rawValue);
    if (Array.isArray(saved)) return dedupeItems(saved);
  } catch (_error) {}

  return [];
}

function readProductCardStorage() {
  try {
    const parsed = safeParse(localStorage.getItem(PRODUCT_CARD_STORAGE_KEY));
    if (!Array.isArray(parsed)) return null;

    return parsed
      .map((value) => normalizeHandle(value))
      .filter(Boolean);
  } catch (_error) {
    return null;
  }
}

function migrateLegacyStorage() {
  const current = readStorage();

  /*
   * An existing empty primary wishlist is intentional.
   * Do not restore removed products from an older storage key.
   */
  if (current !== null) return current;

  const migrated = [];

  for (const key of LEGACY_KEYS) {
    try {
      const parsed = safeParse(localStorage.getItem(key));
      if (Array.isArray(parsed)) migrated.push(...parsed);
      if (parsed && Array.isArray(parsed.items)) migrated.push(...parsed.items);
      if (parsed && Array.isArray(parsed.products)) migrated.push(...parsed.products);
    } catch (_error) {}
  }

  return writeStorage(dedupeItems(migrated), false);
}

function syncFromProductCardStorage() {
  const handles = readProductCardStorage();
  if (handles === null) return Wishlist.getItems();

  const currentItems = Wishlist.getItems();
  const currentByHandle = new Map(
    currentItems.map((item) => [item.handle, item])
  );

  const syncedItems = handles.map((handle) => {
    return currentByHandle.get(handle) || {
      handle,
      addedAt: Date.now(),
    };
  });

  return writeStorage(syncedItems, false);
}

function writeStorage(items, announce = true) {
  const normalized = dedupeItems(items);
  const handles = normalized.map((item) => item.handle);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    localStorage.setItem(PRODUCT_CARD_STORAGE_KEY, JSON.stringify(handles));
  } catch (_error) {}

  if (announce) {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        bubbles: true,
        detail: {
          items: normalized,
          handles,
          count: normalized.length,
          source: 'rd-wishlist',
        },
      })
    );
  }

  return normalized;
}

async function fetchProduct(handle) {
  const response = await fetch(`${getRootUrl()}products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Unable to load product: ${handle}`);
  return response.json();
}

function productToStoredItem(product, preferredVariantId = '') {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const preferredVariant = variants.find(
    (variant) => String(variant.id) === String(preferredVariantId) && variant.available
  );
  const selectedVariant = preferredVariant || variants.find((variant) => variant.available) || variants[0] || null;
  const featuredImage =
    selectedVariant?.featured_image?.src ||
    selectedVariant?.featured_image ||
    product?.featured_image ||
    product?.images?.[0] ||
    '';

  return {
    handle: product.handle,
    productId: product.id || '',
    variantId: selectedVariant?.id || '',
    title: product.title || '',
    url: product.url || `${getRootUrl()}products/${product.handle}`,
    image: featuredImage,
    price: Number(selectedVariant?.price ?? product.price ?? 0),
    compareAtPrice: Number(selectedVariant?.compare_at_price ?? product.compare_at_price ?? 0),
    available: Boolean(selectedVariant?.available ?? product.available),
    addedAt: Date.now(),
  };
}

const Wishlist = {
  getItems() {
    const items = readStorage();
    return items === null ? migrateLegacyStorage() : items;
  },

  has(handle) {
    const normalizedHandle = normalizeHandle(handle);
    return this.getItems().some((item) => item.handle === normalizedHandle);
  },

  add(handle, preferredVariantId = '') {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return this.getItems();

    const items = this.getItems();
    if (items.some((item) => item.handle === normalizedHandle)) return items;

    const placeholderItem = {
      handle: normalizedHandle,
      variantId: preferredVariantId || '',
      addedAt: Date.now(),
    };

    const nextItems = writeStorage([placeholderItem, ...items]);

    /*
     * Save immediately so one tap always feels responsive.
     * Product data is enriched in the background only if the item
     * has not been removed before the request finishes.
     */
    fetchProduct(normalizedHandle)
      .then((product) => {
        const currentItems = this.getItems();
        if (!currentItems.some((item) => item.handle === normalizedHandle)) return;

        const enrichedItem = productToStoredItem(product, preferredVariantId);
        enrichedItem.addedAt = placeholderItem.addedAt;

        writeStorage(
          currentItems.map((item) =>
            item.handle === normalizedHandle ? enrichedItem : item
          ),
          false
        );
      })
      .catch(() => {});

    return nextItems;
  },

  remove(handle) {
    const normalizedHandle = normalizeHandle(handle);
    return writeStorage(this.getItems().filter((item) => item.handle !== normalizedHandle));
  },

  async toggle(handle, preferredVariantId = '') {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) return { active: false, items: this.getItems() };

    if (this.has(normalizedHandle)) {
      return { active: false, items: this.remove(normalizedHandle) };
    }

    return { active: true, items: await this.add(normalizedHandle, preferredVariantId) };
  },

  clear() {
    return writeStorage([]);
  },
};

window.RDWishlist = Wishlist;

function resolveElementHandle(element) {
  const directHandle = normalizeHandle(element.dataset.wishlistHandle || element.dataset.productHandle || '');
  if (directHandle) return directHandle;

  const productOwner = element.closest(
    '[data-wishlist-handle], [data-product-handle], [data-rd-product-card], product-card'
  );

  const ownerHandle = normalizeHandle(
    productOwner?.dataset?.wishlistHandle || productOwner?.dataset?.productHandle || ''
  );
  if (ownerHandle) return ownerHandle;

  const cardLink = productOwner?.querySelector(
    '.product-card__link[href], .rd-product-card__image-link[href], a[href*="/products/"]'
  );
  const cardHandle = getHandleFromUrl(cardLink?.getAttribute('href') || '');
  if (cardHandle) return cardHandle;

  const closestProductLink = element.closest('a[href*="/products/"]');
  const linkHandle = getHandleFromUrl(closestProductLink?.getAttribute('href') || '');
  if (linkHandle) return linkHandle;

  return getHandleFromUrl(window.location.pathname);
}

function resolveVariantId(element) {
  return (
    element.dataset.variantId ||
    element.closest('[data-variant-id]')?.dataset.variantId ||
    document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value ||
    ''
  );
}

function updateToggleState(element) {
  const handle = resolveElementHandle(element);
  const active = Boolean(handle && Wishlist.has(handle));

  element.classList.toggle('is-active', active);
  element.classList.toggle('is-saved', active);
  element.setAttribute('aria-pressed', String(active));
  element.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  element.setAttribute('title', active ? 'Remove from wishlist' : 'Add to wishlist');

  const label = element.querySelector('[data-rd-wishlist-label]');
  if (label) label.textContent = active ? 'Saved to Wishlist' : 'Add to Wishlist';
}

async function toggleWishlistElement(button) {
  if (!(button instanceof HTMLElement) || button.dataset.wishlistLoading === 'true') return;

  const handle = resolveElementHandle(button);
  if (!handle) return;

  button.dataset.wishlistLoading = 'true';
  button.classList.add('is-loading');

  try {
    await Wishlist.toggle(handle, resolveVariantId(button));
    updateAllWishlistUI();
  } finally {
    delete button.dataset.wishlistLoading;
    button.classList.remove('is-loading');
  }
}

function getWishlistToggleFromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return null;

  const element = target.closest(
    [
      '[data-rd-wishlist-toggle]',
      '[data-rd-wishlist]',
      '.rd-product-card__wishlist',
      '[data-wishlist-button]',
      '[data-add-to-wishlist]',
      '[class*="ai-luxury-pdp-wishlist-"]',
      '.wishlist-button',
      '.product-wishlist',
    ].join(',')
  );

  if (!(element instanceof HTMLElement)) return null;
  if (element.matches('.rd-wishlist-header-link, [data-wishlist-remove]')) return null;

  return element;
}

function handleCapturedWishlistClick(event) {
  const button = getWishlistToggleFromEvent(event);
  if (!button) return;

  /*
   * Capture phase is intentional. It stops older section-level wishlist
   * listeners before they can toggle the same product a second time.
   */
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  toggleWishlistElement(button);
}

function bindToggle(element) {
  if (!(element instanceof HTMLElement) || element.dataset.rdWishlistReady === 'true') return;

  const handle = resolveElementHandle(element);
  if (!handle) return;

  element.dataset.rdWishlistReady = 'true';
  element.dataset.wishlistHandle = handle;

  if (element.tagName !== 'BUTTON' && element.tagName !== 'A') {
    element.setAttribute('role', 'button');
    element.setAttribute('tabindex', '0');
    element.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        element.click();
      }
    });
  }

  updateToggleState(element);
}

function createCardToggle(card) {
  if (card.querySelector('[data-rd-wishlist-toggle]')) return;

  const cardLink = card.querySelector('.product-card__link[href], a[href*="/products/"]');
  const handle = getHandleFromUrl(cardLink?.getAttribute('href') || '');
  if (!handle) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'rd-wishlist-toggle rd-wishlist-toggle--card';
  button.dataset.rdWishlistToggle = '';
  button.dataset.wishlistHandle = handle;
  button.innerHTML = heartIcon;

  card.appendChild(button);
  bindToggle(button);
}

function enhanceProductCards(root = document) {
  root.querySelectorAll('product-card[data-product-id]').forEach(createCardToggle);
}

function enhanceExistingProductWishlist(root = document) {
  const selectors = [
    '[data-rd-wishlist]',
    '.rd-product-card__wishlist',
    '[data-wishlist-button]',
    '[data-add-to-wishlist]',
    '[class*="ai-luxury-pdp-wishlist-"]',
    '.wishlist-button',
    '.product-wishlist',
  ];

  root.querySelectorAll(selectors.join(',')).forEach((element) => {
    if (element.matches('[data-rd-wishlist-toggle], .rd-wishlist-header-link')) return;

    element.dataset.rdWishlistToggle = '';
    element.classList.add('rd-wishlist-pdp-toggle');

    const text = element.querySelector('span');
    if (text) text.dataset.rdWishlistLabel = '';

    bindToggle(element);
  });
}

function createHeaderLink(actions) {
  const link = document.createElement('a');
  link.href = WISHLIST_URL;
  link.className = 'header-actions__action rd-wishlist-header-link';
  link.dataset.rdWishlistLink = '';
  link.setAttribute('aria-label', 'Wishlist');
  link.innerHTML = `
    <span class="svg-wrapper" aria-hidden="true">${bookmarkIcon}</span>
    <span class="rd-wishlist-header-count" data-rd-wishlist-count hidden>0</span>
  `;

  const cartTarget = actions.querySelector('cart-drawer-component, .action__cart, [data-testid="cart-drawer-trigger"]');
  if (cartTarget) actions.insertBefore(link, cartTarget);
  else actions.appendChild(link);

  return link;
}

function ensureHeaderWishlist() {
  document.querySelectorAll('header-actions').forEach((actions) => {
    let link = actions.querySelector(
      '[data-rd-wishlist-link], a[href*="/pages/wishlist"], a[href$="/wishlist"], a[href*="wishlist"]'
    );

    if (!link) link = createHeaderLink(actions);

    link.classList.add('rd-wishlist-header-link');
    link.dataset.rdWishlistLink = '';

    if (!link.querySelector('[data-rd-wishlist-count]')) {
      const count = document.createElement('span');
      count.className = 'rd-wishlist-header-count';
      count.dataset.rdWishlistCount = '';
      count.hidden = true;
      count.textContent = '0';
      link.appendChild(count);
    }
  });
}

function updateCounts() {
  const count = Wishlist.getItems().length;

  document.querySelectorAll('[data-rd-wishlist-count]').forEach((element) => {
    element.textContent = String(count);
    element.hidden = count === 0;
  });
}

function updateAllWishlistUI() {
  updateCounts();
  document.querySelectorAll('[data-rd-wishlist-toggle]').forEach(updateToggleState);
}

function formatMoney(cents, currency) {
  const amount = Number(cents || 0) / 100;

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch (_error) {
    return `₹${amount.toFixed(Number.isInteger(amount) ? 0 : 2)}`;
  }
}

function buildProductUrl(product, handle) {
  const productUrl = product?.url || `${getRootUrl()}products/${handle}`;
  try {
    return new URL(productUrl, window.location.origin).pathname;
  } catch (_error) {
    return productUrl;
  }
}

function createWishlistCard(product, storedItem, page) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const preferredVariant = variants.find(
    (variant) => String(variant.id) === String(storedItem.variantId) && variant.available
  );
  const selectedVariant = preferredVariant || variants.find((variant) => variant.available) || variants[0] || null;
  const available = Boolean(selectedVariant?.available ?? product?.available ?? storedItem.available);
  const price = Number(selectedVariant?.price ?? product?.price ?? storedItem.price ?? 0);
  const compareAtPrice = Number(
    selectedVariant?.compare_at_price ?? product?.compare_at_price ?? storedItem.compareAtPrice ?? 0
  );
  const onSale = compareAtPrice > price;
  const discount = onSale ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice) : 0;
  const title = product?.title || storedItem.title || storedItem.handle;
  const url = buildProductUrl(product, storedItem.handle);
  const vendor = product?.vendor || '';
  const hasOptions = variants.length > 1;
  const currency = page.dataset.currency || 'INR';

  const preferredImage =
    selectedVariant?.featured_image?.src ||
    selectedVariant?.featured_image ||
    product?.featured_image ||
    storedItem.image ||
    '';

  const productImages = Array.isArray(product?.images) ? product.images : [];
  const images = [];

  [preferredImage, ...productImages].forEach((value) => {
    const source = typeof value === 'string' ? value : value?.src || '';
    if (source && !images.includes(source)) images.push(source);
  });

  const card = document.createElement('article');
  card.className = 'rd-wishlist-card';
  card.dataset.wishlistCard = '';
  card.dataset.handle = storedItem.handle;

  const media = document.createElement('div');
  media.className = 'rd-wishlist-card__media';

  const gallery = document.createElement('div');
  gallery.className = 'rd-wishlist-card__gallery';

  const galleryTrack = document.createElement('div');
  galleryTrack.className = 'rd-wishlist-card__gallery-track';

  if (images.length) {
    images.slice(0, 5).forEach((source, imageIndex) => {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'rd-wishlist-card__image-wrap';

      const imageLink = document.createElement('a');
      imageLink.className = 'rd-wishlist-card__image-link';
      imageLink.href = url;
      imageLink.setAttribute('aria-label', title);

      const image = document.createElement('img');
      image.className = 'rd-wishlist-card__image';
      image.src = source;
      image.alt = title;
      image.loading = imageIndex === 0 ? 'eager' : 'lazy';
      image.width = 900;
      image.height = 1125;

      imageLink.appendChild(image);
      imageWrap.appendChild(imageLink);
      galleryTrack.appendChild(imageWrap);
    });
  } else {
    const imageWrap = document.createElement('div');
    imageWrap.className = 'rd-wishlist-card__image-wrap';

    const placeholder = document.createElement('a');
    placeholder.className = 'rd-wishlist-card__placeholder';
    placeholder.href = url;
    placeholder.setAttribute('aria-label', title);
    placeholder.textContent = title.slice(0, 1).toUpperCase();

    imageWrap.appendChild(placeholder);
    galleryTrack.appendChild(imageWrap);
  }

  gallery.appendChild(galleryTrack);
  media.appendChild(gallery);

  if (onSale) {
    const badge = document.createElement('span');
    badge.className = 'rd-wishlist-card__badge';
    badge.textContent = `${discount}% OFF`;
    media.appendChild(badge);
  } else if (!available) {
    const badge = document.createElement('span');
    badge.className = 'rd-wishlist-card__badge';
    badge.textContent = page.dataset.soldOutLabel || 'Sold out';
    media.appendChild(badge);
  }

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'rd-wishlist-card__remove is-saved';
  remove.dataset.wishlistRemove = storedItem.handle;
  remove.setAttribute('aria-label', page.dataset.removeLabel || 'Remove from wishlist');
  remove.setAttribute('title', page.dataset.removeLabel || 'Remove from wishlist');
  remove.innerHTML = bookmarkIcon;
  media.appendChild(remove);

  const visibleImages = Math.min(images.length || 1, 5);

  if (visibleImages > 1) {
    const previousButton = document.createElement('button');
    previousButton.type = 'button';
    previousButton.className = 'rd-wishlist-card__gallery-arrow rd-wishlist-card__gallery-arrow--prev';
    previousButton.setAttribute('aria-label', 'Previous product image');
    previousButton.innerHTML = `
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M12.5 4L6.5 10L12.5 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    `;

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'rd-wishlist-card__gallery-arrow rd-wishlist-card__gallery-arrow--next';
    nextButton.setAttribute('aria-label', 'Next product image');
    nextButton.innerHTML = `
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M7.5 4L13.5 10L7.5 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    `;

    const dots = document.createElement('div');
    dots.className = 'rd-wishlist-card__dots';

    const dotElements = [];
    for (let dotIndex = 0; dotIndex < visibleImages; dotIndex += 1) {
      const dot = document.createElement('span');
      dot.className = `rd-wishlist-card__dot${dotIndex === 0 ? ' is-active' : ''}`;
      dotElements.push(dot);
      dots.appendChild(dot);
    }

    let galleryIndex = 0;

    const showImage = (targetIndex) => {
      if (targetIndex < 0) galleryIndex = visibleImages - 1;
      else if (targetIndex >= visibleImages) galleryIndex = 0;
      else galleryIndex = targetIndex;

      galleryTrack.style.transform = `translate3d(${-galleryIndex * 100}%, 0, 0)`;
      dotElements.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === galleryIndex);
      });
    };

    previousButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      showImage(galleryIndex - 1);
    });

    nextButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      showImage(galleryIndex + 1);
    });

    media.append(previousButton, nextButton, dots);
  }

  const body = document.createElement('div');
  body.className = 'rd-wishlist-card__body';

  if (vendor && page.dataset.showVendor === 'true') {
    const vendorElement = document.createElement('p');
    vendorElement.className = 'rd-wishlist-card__vendor';
    vendorElement.textContent = vendor;
    body.appendChild(vendorElement);
  }

  const productText = document.createElement('div');
  productText.className = 'rd-wishlist-card__text';

  const heading = document.createElement('h3');
  heading.className = 'rd-wishlist-card__title';

  const titleLink = document.createElement('a');
  titleLink.href = url;
  titleLink.textContent = title;
  titleLink.title = title;
  heading.appendChild(titleLink);

  const priceRow = document.createElement('div');
  priceRow.className = 'rd-wishlist-card__price';

  const currentPrice = document.createElement('span');
  currentPrice.className = 'rd-wishlist-card__price-current';
  currentPrice.textContent = `(${formatMoney(price, currency)})`;
  priceRow.appendChild(currentPrice);

  if (onSale) {
    const comparePrice = document.createElement('s');
    comparePrice.className = 'rd-wishlist-card__compare';
    comparePrice.textContent = formatMoney(compareAtPrice, currency);
    priceRow.appendChild(comparePrice);
  }

  productText.append(heading, priceRow);
  body.appendChild(productText);

  const action = document.createElement(hasOptions ? 'a' : 'button');
  action.className = 'rd-wishlist-card__action';

  const actionIcon = document.createElement('span');
  actionIcon.className = 'rd-wishlist-card__action-icon';
  actionIcon.setAttribute('aria-hidden', 'true');
  actionIcon.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6.75 8.25H17.25L18 20H6L6.75 8.25Z"></path>
      <path d="M9 8.25V6.75C9 5.09 10.34 3.75 12 3.75C13.66 3.75 15 5.09 15 6.75V8.25"></path>
    </svg>
  `;

  const actionLabel = document.createElement('span');
  actionLabel.dataset.rdWishlistActionLabel = '';

  if (!available) {
    if (action instanceof HTMLButtonElement) action.type = 'button';
    action.setAttribute('aria-disabled', 'true');
    action.classList.add('is-disabled');
    actionLabel.textContent = page.dataset.soldOutLabel || 'Sold out';
  } else if (hasOptions) {
    action.href = url;
    actionLabel.textContent = page.dataset.chooseOptionsLabel || 'Choose options';
  } else {
    action.type = 'button';
    action.dataset.wishlistAddToCart = String(selectedVariant?.id || storedItem.variantId || '');
    action.dataset.productId = String(product?.id || storedItem.productId || '');
    actionLabel.textContent = page.dataset.addToCartLabel || 'Add to cart';
  }

  action.append(actionIcon, actionLabel);
  body.appendChild(action);
  card.append(media, body);
  return card;
}

function setWishlistPageState(page, count) {
  const grid = page.querySelector('[data-rd-wishlist-grid]');
  const empty = page.querySelector('[data-rd-wishlist-empty]');
  const loading = page.querySelector('[data-rd-wishlist-loading]');
  const controls = page.querySelector('[data-rd-wishlist-controls]');
  const countElement = page.querySelector('[data-rd-wishlist-page-count]');

  if (countElement) countElement.textContent = String(count);
  if (grid) grid.hidden = count === 0;
  if (empty) empty.hidden = count !== 0;
  if (controls) controls.hidden = count === 0;
  if (loading) loading.hidden = true;
}

const wishlistPageRenderTimers = new WeakMap();

function scheduleWishlistPageRender(page, delay = 0) {
  if (!(page instanceof HTMLElement)) return;

  const existingTimer = wishlistPageRenderTimers.get(page);
  if (existingTimer) window.clearTimeout(existingTimer);

  const timer = window.setTimeout(() => {
    wishlistPageRenderTimers.delete(page);
    renderWishlistPage(page);
  }, delay);

  wishlistPageRenderTimers.set(page, timer);
}

async function renderWishlistPage(page) {
  if (!(page instanceof HTMLElement)) return;

  const grid = page.querySelector('[data-rd-wishlist-grid]');
  const loading = page.querySelector('[data-rd-wishlist-loading]');
  const items = Wishlist.getItems();

  if (!grid) return;
  grid.innerHTML = '';
  if (loading) loading.hidden = items.length === 0;

  if (!items.length) {
    setWishlistPageState(page, 0);
    return;
  }

  const results = await Promise.all(
    items.map(async (item) => {
      try {
        const product = await fetchProduct(item.handle);
        return { product, item };
      } catch (_error) {
        return { product: null, item };
      }
    })
  );

  const validItems = [];

  for (const result of results) {
    if (!result.product && !result.item.title && !result.item.image) continue;
    validItems.push(result.item);
    grid.appendChild(createWishlistCard(result.product, result.item, page));
  }

  if (validItems.length !== items.length) writeStorage(validItems);
  setWishlistPageState(page, validItems.length);
}

async function addWishlistVariantToCart(button) {
  const variantId = button.dataset.wishlistAddToCart;
  if (!variantId || button.dataset.loading === 'true') return;

  const label = button.querySelector('[data-rd-wishlist-action-label]');
  const originalText = label?.textContent || button.textContent;
  const setLabel = (value) => {
    if (label) label.textContent = value;
    else button.textContent = value;
  };

  button.dataset.loading = 'true';
  button.disabled = true;
  button.classList.add('is-loading');
  setLabel(button.closest('[data-rd-wishlist-page]')?.dataset.addingLabel || 'Adding…');

  try {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

    if (sectionIds.length) formData.append('sections', [...new Set(sectionIds)].join(','));
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(window.Theme?.routes?.cart_add_url || `${getRootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
      credentials: 'same-origin',
    });
    const data = await response.json();

    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add product');

    setLabel(button.closest('[data-rd-wishlist-page]')?.dataset.addedLabel || 'Added ✓');

    button.dispatchEvent(
      new CartAddEvent(data, variantId, {
        source: 'product-form-component',
        itemCount: 1,
        productId: button.dataset.productId || '',
        variantId,
        sections: data.sections || {},
      })
    );

    window.setTimeout(() => {
      setLabel(originalText);
      button.disabled = false;
      button.classList.remove('is-loading');
      delete button.dataset.loading;
    }, 1200);
  } catch (error) {
    setLabel(error instanceof Error ? error.message : 'Unable to add');
    window.setTimeout(() => {
      setLabel(originalText);
      button.disabled = false;
      button.classList.remove('is-loading');
      delete button.dataset.loading;
    }, 1800);
  }
}

function bindWishlistPage(page) {
  if (!(page instanceof HTMLElement) || page.dataset.rdWishlistPageReady === 'true') return;
  page.dataset.rdWishlistPageReady = 'true';

  page.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const removeButton = target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const handle = normalizeHandle(
        removeButton.getAttribute('data-wishlist-remove') || ''
      );

      if (!handle || removeButton.dataset.removing === 'true') return;

      removeButton.dataset.removing = 'true';
      removeButton.disabled = true;

      const card = removeButton.closest('[data-wishlist-card]');
      card?.classList.add('is-removing');

      Wishlist.remove(handle);
      updateAllWishlistUI();
      scheduleWishlistPageRender(page, 140);

      return;
    }

    const clearButton = target.closest('[data-rd-wishlist-clear]');
    if (clearButton) {
      event.preventDefault();
      Wishlist.clear();
      scheduleWishlistPageRender(page);
      return;
    }

    const addButton = target.closest('[data-wishlist-add-to-cart]');
    if (addButton instanceof HTMLButtonElement) {
      await addWishlistVariantToCart(addButton);
    }
  });

  renderWishlistPage(page);
}

function initWishlistPages(root = document) {
  root.querySelectorAll('[data-rd-wishlist-page]').forEach(bindWishlistPage);
}

function initialize(root = document) {
  ensureHeaderWishlist();
  enhanceProductCards(root);
  enhanceExistingProductWishlist(root);
  initWishlistPages(root);
  updateAllWishlistUI();
}

let mutationFrame = 0;
const observer = new MutationObserver((mutations) => {
  if (!mutations.some((mutation) => mutation.addedNodes.length)) return;
  cancelAnimationFrame(mutationFrame);
  mutationFrame = requestAnimationFrame(() => initialize(document));
});

document.addEventListener('click', handleCapturedWishlistClick, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
} else {
  initialize(document);
}

observer.observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener(CHANGE_EVENT, (event) => {
  /*
   * Events without our source marker came from older section code.
   * Import those handles once; our own writes are already synchronised.
   */
  if (event.detail?.source !== 'rd-wishlist') {
    syncFromProductCardStorage();
  }

  updateAllWishlistUI();

  document.querySelectorAll('[data-rd-wishlist-page]').forEach((page) => {
    scheduleWishlistPageRender(page);
  });
});
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
window.addEventListener('storage', (event) => {
  if (event.key === PRODUCT_CARD_STORAGE_KEY) {
    syncFromProductCardStorage();
  } else if (event.key !== STORAGE_KEY && LEGACY_KEYS.includes(event.key)) {
    migrateLegacyStorage();
  }

  if (event.key === STORAGE_KEY || LEGACY_KEYS.includes(event.key)) {
    updateAllWishlistUI();

    document.querySelectorAll('[data-rd-wishlist-page]').forEach((page) => {
      scheduleWishlistPageRender(page);
    });
  }
});
